"use client";
// Cleto, el asistente: burbuja flotante que saluda, abre un chat y "habla" mientras responde.
// Con servidor usa /api/bot (Gemini). En la vista previa estática responde con el catálogo (modo demostración).
import { Fragment, useEffect, useRef, useState } from "react";
import type { Idioma } from "@/content/catalogo";
import { respuestaDemo } from "@/lib/bot";
import { rutaExport } from "./Enlace";
import { usePieVisible } from "@/components/usePieVisible";
import { usePathname } from "next/navigation";
import { Img } from "@/components/Img";

/** Una página le pasa a Cleto lo que debe decir (p. ej. el consejo de una cabaña). Si no, dice el de su sección. */
let ultimoDicho: { ruta: string; texto: string } | null = null;
export function CletoDice({ texto }: { texto: string }) {
  useEffect(() => {
    ultimoDicho = { ruta: window.location.pathname, texto };
    window.dispatchEvent(new CustomEvent("cleto-dice", { detail: texto }));
    return () => { ultimoDicho = null; };
  }, [texto]);
  return null;
}
const DICHOS: [RegExp, string, string][] = [
  [/^\/(es|en)\/?$/, "¿Primera vez en Mazamitla? Te armo el día con tours, cabaña y comida.", "First time in Mazamitla? I can plan your day: tours, cabin and food."],
  [/\/tours\/[^/]+/, "El camión sale puntual: llega 15 minutos antes.", "The truck leaves on time: arrive 15 minutes early."],
  [/\/tours\/?$/, "Elige una fecha y te enseño qué salidas tienen lugar.", "Pick a date and I'll show you which departures have seats."],
  [/\/cabanas\/?$/, "Filtra por alberca, jacuzzi o mascotas y míralas en el mapa.", "Filter by pool, hot tub or pets and see them on the map."],
  [/\/itinerario/, "Si algo quedó en otro día, aquí mismo te aviso.", "If something landed on another day, I'll let you know right here."],
  [/\/(restaurantes|caballos|bar)/, "¿No sabes cuál elegir? Pregúntame y te recomiendo.", "Can't decide? Ask me and I'll recommend one."],
  [/\/(carrito|compra|pago)/, "Un solo pago y te llegan todos tus boletos con QR.", "One payment and all your QR tickets arrive together."],
];

type Msg = { rol: "yo" | "cleto"; texto: string };

export function AbrirBot({ texto }: { texto: string }) {
  return <button type="button" className="boton" onClick={() => window.dispatchEvent(new Event("abrir-cleto"))}>{texto}</button>;
}

function ConLigas({ texto }: { texto: string }) {
  const partes = texto.split(/(\[[^\]]+\]\([^)]+\))/g);
  return (
    <>
      {partes.map((p, i) => {
        const m = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (!m) return <Fragment key={i}>{p.replace(/\*\*/g, "")}</Fragment>;
        const href = m[2].startsWith("/") ? (process.env.NEXT_PUBLIC_EXPORT === "1" ? rutaExport(m[2]) : m[2]) : m[2];
        return <a key={i} href={href} {...(href.startsWith("http") ? { target: "_blank", rel: "noopener" } : {})}>{m[1]}</a>;
      })}
    </>
  );
}

export function BotCleto({ idioma }: { idioma: Idioma }) {
  const es = idioma === "es";
  const [abierto, setAbierto] = useState(false);
  // Con el chat abierto no aparecen ofertas encima (ver .cleto-abierto en sitio.css).
  useEffect(() => { document.body.classList.toggle("cleto-abierto", abierto); return () => document.body.classList.remove("cleto-abierto"); }, [abierto]);
  const [msgs, setMsgs] = useState<Msg[]>([{ rol: "cleto", texto: es ? "¡Hola! Soy Cleto. ¿Qué se te antoja hacer en Mazamitla?" : "Hi! I'm Cleto. What would you like to do in Mazamitla?" }]);
  const [texto, setTexto] = useState("");
  const [pensando, setPensando] = useState(false);
  const [escribiendo, setEscribiendo] = useState(false);
  const [demo, setDemo] = useState(false);
  // Sobre el hero de la portada el botón no flota encima del buscador: aparece al bajar.
  const [sobreHero, setSobreHero] = useState(false);
  // Mensaje de Cleto según la página: aparece animado junto a «¿Te ayudo?» y se va solo. Si lo esconden, no habla.
  const ruta = usePathname();
  const [dicho, setDicho] = useState<string | null>(null);
  useEffect(() => {
    const porDefecto = DICHOS.find(([re]) => re.test(ruta ?? ""));
    let texto = ultimoDicho?.ruta === window.location.pathname ? ultimoDicho.texto : porDefecto ? (es ? porDefecto[1] : porDefecto[2]) : null;
    const nuevo = (e: Event) => { texto = (e as CustomEvent<string>).detail; };
    window.addEventListener("cleto-dice", nuevo);
    // En el teléfono el globo taparía botones y filtros: ahí Cleto no interrumpe; habla cuando lo tocan. En compu se va al bajar la página.
    if (window.matchMedia("(max-width: 760px)").matches) { window.removeEventListener("cleto-dice", nuevo); return; }
    const callar = () => setDicho(null);
    const entra = setTimeout(() => { if (texto) setDicho(texto); }, 1600), sale = setTimeout(callar, 8000);
    const alBajar = () => { if (window.scrollY > 300) callar(); };
    window.addEventListener("scroll", alBajar, { passive: true });
    return () => { clearTimeout(entra); clearTimeout(sale); window.removeEventListener("cleto-dice", nuevo); window.removeEventListener("scroll", alBajar); setDicho(null); };
  }, [ruta, es]);
  // Se puede esconder: queda una pestañita pegada al borde y se vuelve a abrir con un toque. Se recuerda en este navegador.
  const [escondido, setEscondido] = useState(false);
  useEffect(() => { try { setEscondido(localStorage.getItem("cleto-escondido") === "1"); } catch { /* sin almacenamiento */ } }, []);
  const esconder = (v: boolean) => { setEscondido(v); if (v) setAbierto(false); try { localStorage.setItem("cleto-escondido", v ? "1" : "0"); } catch { /* sin almacenamiento */ } };
  useEffect(() => {
    const f = () => setSobreHero(!!document.querySelector(".si-hero") && window.scrollY < window.innerHeight * 0.6);
    f(); window.addEventListener("scroll", f, { passive: true }); window.addEventListener("resize", f);
    return () => { window.removeEventListener("scroll", f); window.removeEventListener("resize", f); };
  }, []);
  const caja = useRef<HTMLDivElement>(null);
  const pieVisible = usePieVisible();

  useEffect(() => {
    const abrir = () => setAbierto(true);
    window.addEventListener("abrir-cleto", abrir);
    return () => window.removeEventListener("abrir-cleto", abrir);
  }, []);
  useEffect(() => { caja.current?.scrollTo({ top: caja.current.scrollHeight, behavior: "smooth" }); }, [msgs, pensando]);
  useEffect(() => { if (pieVisible) setAbierto(false); }, [pieVisible]);

  async function escribir(respuesta: string) {
    setEscribiendo(true);
    setMsgs((m) => [...m, { rol: "cleto", texto: "" }]);
    const rapido = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    for (let i = rapido ? respuesta.length : 1; i <= respuesta.length; i += 2) {
      setMsgs((m) => [...m.slice(0, -1), { rol: "cleto", texto: respuesta.slice(0, i) }]);
      await new Promise((r) => setTimeout(r, 14));
    }
    setMsgs((m) => [...m.slice(0, -1), { rol: "cleto", texto: respuesta }]);
    setEscribiendo(false);
  }

  async function enviar(pregunta: string) {
    const q = pregunta.trim();
    if (!q || pensando || escribiendo) return;
    const historial: Msg[] = [...msgs, { rol: "yo", texto: q }];
    setMsgs(historial);
    setTexto("");
    setPensando(true);
    let respuesta = "";
    try {
      if (demo) throw new Error("demo");
      const r = await fetch("/api/bot", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ idioma, mensajes: historial.slice(-10) }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.mensaje);
      respuesta = d.texto;
    } catch {
      setDemo(true);
      await new Promise((r) => setTimeout(r, 600));
      respuesta = respuestaDemo(q, idioma, historial.slice(0, -1).map((m) => m.texto));
    }
    setPensando(false);
    await escribir(respuesta);
  }

  // Cleto se puede arrastrar para que no tape ligas; se queda pegado al lado más cercano y recuerda dónde lo dejaste.
  const boton = useRef<HTMLButtonElement>(null);
  const movido = useRef(false);
  const inicio = useRef<{ x: number; y: number; abajo: number } | null>(null);
  const [pos, setPos] = useState<{ abajo: number } | null>(null);
  const [lado, setLado] = useState<"der" | "izq">("der");
  const [arrastrando, setArrastrando] = useState(false);
  useEffect(() => { try { const g = JSON.parse(localStorage.getItem("cleto-pos") ?? "null"); if (g) { setPos({ abajo: Math.min(g.abajo, window.innerHeight - 100) }); setLado(g.lado); } } catch { /* sin almacenamiento */ } }, []);
  const empezar = (e: React.PointerEvent) => { inicio.current = { x: e.clientX, y: e.clientY, abajo: pos?.abajo ?? 18 }; movido.current = false; try { boton.current?.setPointerCapture(e.pointerId); } catch { /* sin captura */ } };
  const mover = (e: React.PointerEvent) => {
    const i = inicio.current; if (!i) return;
    const dx = e.clientX - i.x, dy = e.clientY - i.y;
    if (!movido.current && Math.hypot(dx, dy) < 8) return;
    if (!movido.current) { movido.current = true; setArrastrando(true); }
    const el = boton.current!; el.style.left = `${Math.max(4, Math.min(window.innerWidth - 82, e.clientX - 39))}px`; el.style.right = "auto";
    el.style.bottom = `${Math.max(8, Math.min(window.innerHeight - 90, i.abajo - dy))}px`;
  };
  const soltar = (e: React.PointerEvent) => {
    const i = inicio.current; inicio.current = null;
    if (!i || !movido.current) return;
    const el = boton.current!; el.style.left = ""; el.style.right = ""; el.style.bottom = "";
    const nuevo = { abajo: Math.max(8, Math.min(window.innerHeight - 90, i.abajo - (e.clientY - i.y))), lado: e.clientX < window.innerWidth / 2 ? "izq" as const : "der" as const };
    setPos({ abajo: nuevo.abajo }); setLado(nuevo.lado); setArrastrando(false);
    try { localStorage.setItem("cleto-pos", JSON.stringify(nuevo)); } catch { /* sin almacenamiento */ }
  };

  const habla = pensando || escribiendo;
  const sugerencias = es ? ["¿Qué hago con niños?", "Tours de noche", "Paseos a caballo", "¿Y si llueve?"] : ["Things to do with kids", "Night tours", "Horseback rides", "What if it rains?"];

  return (
    <>
      {abierto && (
        <div className={`bot-panel${lado === "izq" ? " izq" : ""}`} role="dialog" aria-label={es ? "Chat con Cleto" : "Chat with Cleto"}>
          <div className="bot-cab">
            <div className={`cara${habla ? " habla" : ""}`}>
              <video key={habla ? "habla" : "quieto"} src={habla ? "/media/s_hola.mp4" : "/media/s_guino.mp4"} poster="/media/cleto_hola.webp" autoPlay muted loop playsInline aria-hidden="true" />
            </div>
            <div>
              <strong>Cleto</strong>
              <small>{habla ? (es ? "escribiendo…" : "typing…") : (es ? "aquí para ayudarte" : "here to help")}</small>
            </div>
            <button type="button" aria-label={es ? "Cerrar" : "Close"} onClick={() => setAbierto(false)}>×</button>
          </div>
          <div className="bot-msgs" ref={caja} aria-live="polite">
            {msgs.map((m, i) => <div key={i} className={`msg ${m.rol}`}><ConLigas texto={m.texto} /></div>)}
            {pensando && <div className="msg cleto"><span className="escribiendo" aria-label="…"><i /><i /><i /></span></div>}
            {msgs.length === 1 && (
              <div className="sugerencias">{sugerencias.map((s) => <button key={s} type="button" onClick={() => enviar(s)}>{s}</button>)}</div>
            )}
          </div>
          <form className="bot-form" onSubmit={(e) => { e.preventDefault(); enviar(texto); }}>
            <label className="sr" htmlFor="bot-texto">{es ? "Tu pregunta" : "Your question"}</label>
            <input id="bot-texto" value={texto} maxLength={500} onChange={(e) => setTexto(e.target.value)} placeholder={es ? "Escribe tu pregunta…" : "Type your question…"} autoComplete="off" />
            <button className="boton" type="submit" disabled={!texto.trim() || habla}>{es ? "Enviar" : "Send"}</button>
          </form>
        </div>
      )}
      {!pieVisible && !sobreHero && escondido && (
        <button type="button" className={`bot-pestana${lado === "izq" ? " izq" : ""}`} onClick={() => esconder(false)} aria-label={es ? "Mostrar a Cleto" : "Show Cleto"} title={es ? "Mostrar a Cleto" : "Show Cleto"}>
          <Img src="/media/cleto_hola.webp" alt="" width={44} height={44} />
        </button>
      )}
      {!pieVisible && !sobreHero && !escondido && <div className={`bot-lanzador${lado === "izq" ? " izq" : ""}`} style={pos ? { [lado === "izq" ? "left" : "right"]: 18, bottom: pos.abajo, ...(lado === "izq" ? { right: "auto" } : {}) } : undefined}>
      <button type="button" ref={boton} className={`bot-boton${lado === "izq" ? " izq" : ""}${arrastrando ? " arrastrando" : ""}`}
        aria-label={es ? "Habla con Cleto (puedes arrastrarlo)" : "Chat with Cleto (you can drag it)"} title={es ? "Tócame para platicar o arrástrame si te estorbo" : "Tap to chat or drag me out of the way"} aria-expanded={abierto}
        onPointerDown={empezar} onPointerMove={mover} onPointerUp={soltar} onPointerCancel={soltar}
        onClick={() => { if (movido.current) { movido.current = false; return; } setAbierto(!abierto); }}>
        <video src="/media/s_hola.mp4" poster="/media/cleto_hola.webp" autoPlay muted loop playsInline aria-hidden="true" />
        {!abierto && (dicho ? <span key={dicho} className="globo dicho" role="status">{dicho}</span> : <span className="globo">{es ? "¿Te ayudo?" : "Need help?"}</span>)}
      </button>
      {!abierto && <button type="button" className="bot-esconder" onClick={() => esconder(true)} aria-label={es ? "Esconder a Cleto" : "Hide Cleto"} title={es ? "Esconder" : "Hide"}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg></button>}
      </div>}
    </>
  );
}
